import React, { useCallback } from 'react';
import Cropper from 'cropperjs';

type UseImageRotationParams = {
    cropperRef: React.RefObject<Cropper>;
    isDirtyRef: React.MutableRefObject<boolean>;
    initialCropDataRef: React.MutableRefObject<Cropper.CropBoxData | null>;
    currentRotation: number;
    initialRotation: number;
    currentScaleX: number;
    currentScaleY: number;
    setCurrentRotation: (val: number) => void;
    setCurrentScaleX: (val: number) => void;
    setCurrentScaleY: (val: number) => void;
    setHideDrawing: (val: boolean) => void;
};

export function useImageRotation({
    cropperRef,
    isDirtyRef,
    initialCropDataRef,
    currentRotation,
    initialRotation,
    currentScaleX,
    currentScaleY,
    setCurrentRotation,
    setCurrentScaleX,
    setCurrentScaleY,
    setHideDrawing,
}: UseImageRotationParams) {
    /**
     * Rotate the Cropper image left by 90 degrees while keeping crop selection state.
     *
     * Rotation is destructive to drawings (the overlay can't follow a quarter-turn), so the drawing is
     * hidden whenever the rotation differs from its starting value and cleared on save. Flipping, by
     * contrast, keeps the drawing and transforms it to match.
     */
    const handleRotateImage = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        isDirtyRef.current = true;

        // Save the current crop data, so we can rotate it on re-render (see useLayoutEffect in ImageModalEditor.ts)
        initialCropDataRef.current = cropper.getCropBoxData();

        cropper.clear();
        cropper.rotate(-90);
        cropper.crop();

        const newRotation = currentRotation === -270 ? 0 : currentRotation - 90;

        setHideDrawing(initialRotation !== newRotation);
        setCurrentRotation(newRotation);
    }, [
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        setCurrentRotation,
        setHideDrawing,
    ]);

    /**
     * Flip the Cropper image horizontally. The drawing overlay flips with it (see ImageModalEditor), so the
     * drawing is preserved rather than hidden.
     */
    const handleFlipImageHorizontally = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        const newScaleX = currentScaleX * -1;
        isDirtyRef.current = true;

        cropper.scaleX(newScaleX);
        setCurrentScaleX(newScaleX);
    }, [cropperRef, currentScaleX, isDirtyRef, setCurrentScaleX]);

    /**
     * Flip the Cropper image vertically. The drawing overlay flips with it, so it is preserved.
     */
    const handleFlipImageVertically = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        const newScaleY = currentScaleY * -1;
        isDirtyRef.current = true;

        cropper.scaleY(newScaleY);
        setCurrentScaleY(newScaleY);
    }, [cropperRef, currentScaleY, isDirtyRef, setCurrentScaleY]);

    return { handleRotateImage, handleFlipImageHorizontally, handleFlipImageVertically };
}
